// What a public ledger signing card shows, from its job's PROCESSED marker and its transactions. Pure, so it can be
// unit-tested, and derived on every update so a realtime change (another signatory signing or rejecting, the status cron
// ending the transaction) switches a card that is already open.
import type { PublicLedgerRejection, PublicLedgerTransaction } from '../models/public-ledger.js';
import type { DeviceProof } from './wallet-key-store.js';

export type CollectSignatureState =
  // Waiting for this person's signature
  | 'PENDING'
  // This person is writing the reason for rejecting
  | 'REJECTING'
  // This person has signed; the others haven't all signed yet
  | 'ALREADY_SIGNED'
  // Enough signatures: the transaction went to the public ledger
  | 'PROCESSED_SUBMITTED'
  // A signatory rejected it, which ended the signing for everyone
  | 'PROCESSED_REJECTED'
  // It ended without anyone rejecting it: it expired, failed on the public ledger or was cancelled
  | 'PROCESSED_ENDED'
  // A part of a long message failed on the public ledger after it was signed, and is to be signed again
  | 'RESIGN_FAILED_PART';

export type SignatureTransaction = Pick<
  PublicLedgerTransaction,
  'id' | 'status' | 'signatories' | 'signatures' | 'rejection' | 'cancelReason' | 'partIndex' | 'replacesTransactionId'
>;

export interface CollectSignatureInput {
  // The job's PROCESSED metadata: SUBMITTED or REJECTED (TRUE on cards made before the two were told apart)
  processed: string | null | undefined;
  // The transaction the job was created for
  transaction: SignatureTransaction | null | undefined;
  // Every transaction this card signs at once (the parts of a long message); the job's own transaction when omitted
  transactions?: SignatureTransaction[];
  personId: string | null | undefined;
  rejecting?: boolean;
  // Why the part this card signs again failed on the public ledger
  failureReason?: string | null;
}

export interface CollectSignatureView {
  state: CollectSignatureState;
  // The rejection that ended the signing (PROCESSED_REJECTED)
  rejection: PublicLedgerRejection | null;
  // How the transaction ended (PROCESSED_ENDED)
  ended: 'EXPIRED' | 'FAILED' | 'CANCELLED' | null;
  cancelReason: string | null;
  // The part signed again, counted from 1, and why it failed (RESIGN_FAILED_PART)
  partNumber: number | null;
  failureReason: string | null;
  // The transactions this person still has to sign
  toSign: SignatureTransaction[];
}

const COLLECTING = 'COLLECTING_SIGNATURES';
const SUBMITTED = ['SIGNED', 'SUCCESS'];
const ENDED = ['EXPIRED', 'FAILED', 'CANCELLED'] as const;

/** Whether this person's signature is already on the transaction. */
export function hasSigned(transaction: SignatureTransaction, personId: string | null | undefined): boolean {
  if (!personId) return false;
  if ((transaction.signatures ?? []).some((s) => s.personId === personId)) return true;
  return (transaction.signatories ?? []).includes(personId);
}

/** The transactions still collecting signatures that this person hasn't signed. */
export function transactionsToSign(
  transactions: SignatureTransaction[],
  personId: string | null | undefined,
): SignatureTransaction[] {
  return transactions.filter((t) => t.status === COLLECTING && !hasSigned(t, personId));
}

type GroupTransaction = Pick<
  PublicLedgerTransaction,
  'id' | 'type' | 'consensusService' | 'partIndex' | 'replacesTransactionId'
>;
const MESSAGE_TYPE = 'SUBMIT_TOPIC_MESSAGE_TRANSACTION';

/**
 * The transactions a card signs at once, as public-ledger-submit-schedule-transaction-signature groups them (signingGroup in
 * ns-lambda-ts): every first-attempt part of a long message, in part order, from the message's transactions; a part signed
 * again signs only itself; any other transaction is signed on its own.
 */
export function signingGroup<T extends GroupTransaction>(lead: T, messageTransactions: T[]): T[] {
  if (lead.type !== MESSAGE_TYPE || !lead.consensusService?.messageId || lead.replacesTransactionId) return [lead];
  const firsts = messageTransactions.filter(
    (t) =>
      t.type === MESSAGE_TYPE &&
      !t.replacesTransactionId &&
      t.consensusService?.messageId === lead.consensusService?.messageId,
  );
  if (!firsts.some((t) => t.id === lead.id)) firsts.push(lead);
  return firsts.sort((a, b) => (a.partIndex ?? 0) - (b.partIndex ?? 0));
}

// A transaction's ScheduleSign bytes, as public-ledger-get-scheduled-transaction-serialisation serves them (unsigned) or
// as this person's browser signed them
export interface SignaturePart {
  transactionId: string;
  bytes: string;
}

/**
 * The served parts this person signs, in the order served. The serialisation lambda serves every part of the signing
 * group still collecting signatures, which can include one this person has signed since the card last updated; the card
 * signs those its view still lists.
 */
export function partsToSign<T extends SignaturePart>(served: T[], toSignIds: string[]): T[] {
  return served.filter((part) => toSignIds.includes(part.transactionId));
}

/**
 * The public-ledger-submit-schedule-transaction-signature body that signs every part in one call: the server checks all
 * of them before it sends any to the public ledger, so a part it refuses stops them all. `device` proves the signature
 * comes from a device that hasn't been signed out (#24 phase 3).
 */
export function signatureBatchBody(
  signed: SignaturePart[],
  ids: { signatoryId: string; workflowId: string; jobId: string },
  device: DeviceProof,
) {
  const [lead] = signed;
  if (!lead) throw new Error('Nothing to sign');
  return {
    transactionId: lead.transactionId,
    transactionBytes: lead.bytes,
    parts: signed.map((part) => ({ transactionId: part.transactionId, transactionBytes: part.bytes })),
    ...ids,
    device,
  };
}

export function collectSignatureState(input: CollectSignatureInput): CollectSignatureView {
  const { processed, transaction, personId } = input;
  const transactions = input.transactions?.length ? input.transactions : transaction ? [transaction] : [];
  const view: CollectSignatureView = {
    state: 'PENDING',
    rejection: null,
    ended: null,
    cancelReason: null,
    partNumber: null,
    failureReason: null,
    toSign: [],
  };

  // One rejection ends the signing of every part
  const rejected = transactions.find((t) => t.status === 'REJECTED');
  if (rejected || processed === 'REJECTED') {
    return { ...view, state: 'PROCESSED_REJECTED', rejection: rejected?.rejection ?? null };
  }

  const ended = ENDED.find((status) => transaction?.status === status);
  if (ended) {
    return { ...view, state: 'PROCESSED_ENDED', ended, cancelReason: transaction?.cancelReason ?? null };
  }

  const allSubmitted = transactions.length > 0 && transactions.every((t) => SUBMITTED.includes(t.status));
  if (processed === 'SUBMITTED' || processed === 'TRUE' || allSubmitted) {
    return { ...view, state: 'PROCESSED_SUBMITTED' };
  }

  const toSign = transactionsToSign(transactions, personId);
  if (toSign.length === 0 && transactions.some((t) => hasSigned(t, personId))) {
    return { ...view, state: 'ALREADY_SIGNED' };
  }

  if (input.rejecting) return { ...view, state: 'REJECTING', toSign };

  if (transaction?.replacesTransactionId && typeof transaction.partIndex === 'number') {
    return {
      ...view,
      state: 'RESIGN_FAILED_PART',
      partNumber: transaction.partIndex + 1,
      failureReason: input.failureReason?.trim() || null,
      toSign,
    };
  }

  return { ...view, toSign };
}

/**
 * The rule a card is signed or approved under (#38): a company-topic message, and a change to who signs the topic, under
 * the topic's own submit key, which can differ from the company account's rule; a change of the account's signatories
 * and a transfer from the company account under the company's rule. "You sign as part of ..." names its groups.
 */
export function ruleSignedUnder(
  transactionType: string | null | undefined,
  rules: { companyRule: unknown; topicSubmitKey: unknown },
): unknown {
  if (transactionType === 'MESSAGE_SUBMIT' || transactionType === 'TOPIC_CHANGE') return rules.topicSubmitKey;
  return rules.companyRule;
}

// Plain words for the public ledger result codes a signed transaction most often fails with
const RESULT_CODE_TEXT: Record<string, string> = {
  INSUFFICIENT_PAYER_BALANCE: "the company's public ledger allowance couldn't pay the fee",
  INSUFFICIENT_ACCOUNT_BALANCE: "the account didn't hold enough nodeStream credits",
  INSUFFICIENT_TX_FEE: 'the fee offered was too low',
  INVALID_SIGNATURE: 'a signature was not valid',
  INVALID_TOPIC_ID: 'the topic was not found',
  TOPIC_EXPIRED: 'the topic has expired',
  INVALID_TOPIC_MESSAGE: 'the message was not accepted',
  MESSAGE_SIZE_TOO_LARGE: 'the message part was too long',
  BUSY: 'the public ledger was busy',
};

/** A result code from the public ledger in plain words, or the code itself, lower-cased, when it is not a common one. */
export function ledgerResultText(code: string | null | undefined): string | null {
  if (!code) return null;
  return RESULT_CODE_TEXT[code] ?? code.toLowerCase().replace(/_/g, ' ');
}
